import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useParams } from 'react-router-dom';
import { useClinicDashboard } from '../context/ClinicDashboardContext';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import CategoryBar from '../components/CategoryBar';
import DateFilter from '../components/DateFilter';
import EmptyState from '../components/EmptyState';
import api from '../../../../services/api';

const AppointmentClinicalFlow = ({ onBack }) => {
    const { clinicId } = useParams();
    const { sharedMonthYear } = useClinicDashboard();
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ year: sharedMonthYear.year });
    const [data, setData] = useState({
        kpis: [],
        statusBars: [],
        providerBars: [],
        timeData: []
    });

    useEffect(() => {
        const fetchAppointmentData = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/api/v1/clinics/${clinicId}/dashboard/appointment-flow?year=${filters.year}`);
                if (response.data?.status === 'success') {
                    setData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching appointment flow data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchAppointmentData();
        }
    }, [clinicId, filters]);

    if (loading) {
        return <div className="p-4">Loading appointment clinical flow...</div>;
    }

    const filterMonthlyChartData = (chartData, year) => {
        const now = new Date();
        if (year < now.getFullYear()) return chartData;
        return chartData.slice(0, now.getMonth() + 1);
    };

    const timeData = filterMonthlyChartData(data.timeData, filters.year);

    const hasStatusBars = data.statusBars.some(b => b.value > 0);
    const hasProviderBars = data.providerBars.some(b => b.value > 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <ViewHeader
                title="Appointment Clinical Flow"
                subtitle={`Year ${filters.year}`}
                onBack={onBack}
                filter={
                    <DateFilter
                        mode="year"
                        value={filters}
                        onChange={setFilters}
                    />
                }
            />

            <div className="clinic-dashboard-growth-grid auto-rows">
                {/* Left column - stacked cards */}
                <div className="clinic-dashboard-growth-left-top clinic-dashboard-section-card" style={{ alignSelf: 'start', animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Key metrics</div>
                    <div className="clinic-dashboard-kpi-row">
                        {data.kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>

                <div className="clinic-dashboard-growth-left-mid clinic-dashboard-section-card" style={{ alignSelf: 'start', animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Appointments status</div>
                    {hasStatusBars ? (
                        <div className="clinic-dashboard-catbar-list medium">
                            {data.statusBars.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                        </div>
                    ) : (
                        <EmptyState message="No status data for this period" />
                    )}
                </div>

                <div className="clinic-dashboard-growth-left-bot clinic-dashboard-section-card" style={{ alignSelf: 'start', animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Appointments by provider</div>
                    {hasProviderBars ? (
                        <div className="clinic-dashboard-catbar-list medium">
                            {data.providerBars.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                        </div>
                    ) : (
                        <EmptyState message="No provider data for this period" />
                    )}
                </div>

                {/* Right column - tall chart spanning all rows */}
                <div className="clinic-dashboard-growth-right clinic-dashboard-section-card" style={{ animationDelay: '240ms' }}>
                    <div className="clinic-dashboard-section-title">Appointments over time</div>
                    <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '260px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={timeData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Line animationBegin={600} type="monotone" dataKey="value" stroke="#0f766e" strokeWidth={2} dot={{ r: 4, fill: '#0f766e' }} activeDot={{ r: 6 }} name="appointments" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentClinicalFlow;
